import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Oval } from 'react-loader-spinner';
import {
  effectVideoUrl,
  getPreloadedEffectVideo,
  whenEffectVideoPreloaded,
  FALLBACK_VIDEO_NAME,
} from '../../utils/media/videoPreload';

// 再生が始まるまでの待機時間(ミリ秒)。ここで調整する。
const VIDEO_LOAD_TIMEOUT_MS = 10000;
// 再生中にバッファ切れで止まったときの待機時間。既に見え始めているので短くする。
const VIDEO_STALL_TIMEOUT_MS = 6000;
// ダウンロードが進んでいる間は諦めずに延長する。チケットを消費した演出を
// あと少しのところで捨てるのが最も避けたい結果のため。
const VIDEO_LOAD_EXTENSION_MS = 5000;
// 何度も停止を繰り返す場合の絶対上限。5秒の動画がこれを超えたら諦める。
const VIDEO_TOTAL_MAX_MS = 30000;

// データセーバーONの場合は動画を再生しない
const shouldSkipVideo = (): boolean => {
  const conn = (navigator as unknown as { connection?: { saveData?: boolean } }).connection;
  return Boolean(conn && conn.saveData);
};

type GachaEffectVideoProps = {
  videoName: string;
  onFinished: () => void;
};

export const GachaEffectVideo: React.FC<GachaEffectVideoProps> = ({ videoName, onFinished }) => {
  const [skipped] = useState<boolean>(shouldSkipVideo);
  const [ready, setReady] = useState(false);
  // 先読み済みの Blob URL か、通常のネットワークURL。解決するまでは null。
  const [videoSrc, setVideoSrc] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const finishedRef = useRef(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const mountedAtRef = useRef(Date.now());
  const lastBufferedRef = useRef(0);

  // onFinished は親でインライン定義され得るため ref 経由で安定参照する
  const onFinishedRef = useRef(onFinished);
  useEffect(() => {
    onFinishedRef.current = onFinished;
  });

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // 演出終了(動画終了・スキップ・読み込み失敗)。結果画面へ遷移する。
  const finish = useCallback(() => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    clearTimer();
    onFinishedRef.current();
  }, [clearTimer]);

  // 再生に使うURLを決める。先読みが進行中の場合はその完了を待つ。
  // ここでネットワークURLを使うと同じ動画を二重にダウンロードしてしまう。
  useEffect(() => {
    if (skipped) return;

    // 名前を確定させてから照会する。未指定時のフォールバック先も先読み対象なので、
    // ここで解決しておかないとキャッシュを引けずに取り直してしまう。
    const name = videoName || FALLBACK_VIDEO_NAME;

    const preloaded = getPreloadedEffectVideo(name);
    if (preloaded) {
      setVideoSrc(preloaded);
      return;
    }

    const pending = whenEffectVideoPreloaded(name);
    if (!pending) {
      setVideoSrc(effectVideoUrl(name));
      return;
    }

    let cancelled = false;
    pending.then((url) => {
      if (!cancelled) {
        setVideoSrc(url ?? effectVideoUrl(name));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [videoName, skipped]);

  // 停止監視タイマー。指定時間内に状況が改善しなければ結果画面へ直行するが、
  // バッファが伸びている間は絶対上限まで延長する。
  // 再生開始前と、再生中にバッファ切れで止まったときの両方で使う。
  const startWatchdog = useCallback((firstDelay: number) => {
    clearTimer();

    const check = () => {
      const video = videoRef.current;
      let progressed = false;

      if (video && video.buffered.length > 0) {
        const end = video.buffered.end(video.buffered.length - 1);
        if (end > lastBufferedRef.current) {
          lastBufferedRef.current = end;
          progressed = true;
        }
      }

      if (progressed && Date.now() - mountedAtRef.current < VIDEO_TOTAL_MAX_MS) {
        timerRef.current = setTimeout(check, VIDEO_LOAD_EXTENSION_MS);
      } else {
        finish();
      }
    };

    timerRef.current = setTimeout(check, firstDelay);
  }, [clearTimer, finish]);

  useEffect(() => {
    if (skipped) {
      // 動画なし = 結果画面へ直行
      finish();
      return;
    }

    setReady(false);
    startWatchdog(VIDEO_LOAD_TIMEOUT_MS);
    return clearTimer;
  }, [skipped, finish, clearTimer, startWatchdog]);

  if (skipped) {
    return null;
  }

  // 再生準備完了(または再生開始)でタイマー解除。
  const handleReady = () => {
    clearTimer();
    setReady(true);
  };

  // 再生中のバッファ切れ。スピナーを出し直して監視を再開する。
  // canplaythrough は「最後まで止まらず再生できそう」というブラウザの推定に
  // すぎないため、再生が始まった後でも普通に止まりうる。
  const handleWaiting = () => {
    const video = videoRef.current;
    // まだ再生が始まっていない場合は初回ロードの監視が効いているので何もしない
    if (!video || video.currentTime === 0) return;
    setReady(false);
    startWatchdog(VIDEO_STALL_TIMEOUT_MS);
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: '#000',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
    }}>
      {videoSrc && (
        <video
          ref={videoRef}
          key={videoSrc}
          src={videoSrc}
          autoPlay
          muted
          playsInline
          preload="auto"
          disablePictureInPicture
          onCanPlayThrough={handleReady}
          onPlaying={handleReady}
          onWaiting={handleWaiting}
          onEnded={finish}
          onError={finish}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            width: '100vw',
            height: '100vh',
            objectFit: 'contain',
            pointerEvents: 'none',
          }}
        />
      )}
      {!ready && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Oval
            height={120}
            width={120}
            color="#ffffff"
            secondaryColor="rgba(255,255,255,0.4)"
            strokeWidth={3}
            strokeWidthSecondary={3}
            visible={true}
            ariaLabel="loading-effect-video"
          />
        </div>
      )}
      <button
        onClick={finish}
        style={{
          position: 'absolute',
          bottom: '2rem',
          right: '2rem',
          padding: '0.5rem 1.5rem',
          backgroundColor: 'rgba(255,255,255,0.2)',
          color: '#fff',
          border: '1px solid rgba(255,255,255,0.5)',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '1rem',
          zIndex: 10000,
        }}
      >
        スキップ
      </button>
    </div>
  );
};

export default GachaEffectVideo;
